import { useState, useRef, useCallback, useEffect } from 'react';
import { motion } from 'framer-motion';
import { SurveySubmittedMessage } from '../shared/SlideShared';
import FeedbackSelector from '../FeedbackSelector';
import { fireCorrect } from '../../../../hooks/use-confetti';
import {
  ActivitySlideData,
  LikertSlideData,
  SurveyChoiceSlideData,
  FreeTextSlideData,
  DropdownSlideData,
  RankingSlideData,
  MatchingSlideData,
  Theme,
  slideTypographyStyle,
} from '../slideTypes';
import { ActivityResponsePayload } from '../../../../types';
import { WrappingInput } from '../shared/WrappingInput';
import {
  springs,
} from '../../../ui/slide-animations';

// --- Likert Scale ---
function LikertActivity({
  data, theme, isEditing, onUpdate, onActivityResponse,
}: {
  data: LikertSlideData; theme: Theme; isEditing: boolean;
  onUpdate: (data: LikertSlideData) => void;
  onActivityResponse?: (response: ActivityResponsePayload) => void;
}) {
  const [selectedValue, setSelectedValue] = useState<number | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const points = Array.from({ length: data.scaleSize }, (_, i) => i + 1);

  if (isEditing) {
    return (
      <div className="h-full p-8 md:p-16 flex flex-col justify-center">
        <WrappingInput
          value={data.question}
          onChange={(question) => onUpdate({ ...data, question })}
          placeholder="Enter your question..."
          className="w-full mb-6 bg-transparent border-0 border-b-2 border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none transition-colors"
          data-slide-level="heading" style={{ ...slideTypographyStyle(theme, 'heading'), color: theme.colors.text }}
        />
        <div className="space-y-4">
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="block text-xs font-medium mb-1" style={{ color: theme.colors.textMuted }}>Low label</label>
              <input type="text" value={data.scaleLabels.low} onChange={(e) => onUpdate({ ...data, scaleLabels: { ...data.scaleLabels, low: e.target.value } })}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" style={{ backgroundColor: theme.colors.surface, color: theme.colors.text }} />
            </div>
            <div className="flex-1">
              <label className="block text-xs font-medium mb-1" style={{ color: theme.colors.textMuted }}>High label</label>
              <input type="text" value={data.scaleLabels.high} onChange={(e) => onUpdate({ ...data, scaleLabels: { ...data.scaleLabels, high: e.target.value } })}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" style={{ backgroundColor: theme.colors.surface, color: theme.colors.text }} />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium mb-1" style={{ color: theme.colors.textMuted }}>Scale points</label>
            <div className="flex gap-2">
              {([5, 7] as const).map((size) => (
                <button key={size} onClick={() => onUpdate({ ...data, scaleSize: size })}
                  className={`px-4 py-2 text-sm rounded-lg border-2 transition-colors ${data.scaleSize === size ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 hover:border-gray-300'}`}
                  style={data.scaleSize !== size ? { color: theme.colors.text } : undefined}>
                  {size} points
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // View mode
  return (
    <div className="h-full flex flex-col justify-center items-center" style={{ padding: theme.spacing.padded }}>
      <div className="slide-content-narrow">
        <motion.h2 className="mb-8 text-center" initial={{ opacity: 0, y: 25 }} animate={{ opacity: 1, y: 0 }} transition={springs.gentle as object}
          data-slide-level="heading" style={{ ...slideTypographyStyle(theme, 'heading'), color: theme.colors.text }}>
          {data.question || 'Likert Scale'}
        </motion.h2>
        <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ ...springs.gentle as object, delay: 0.1 }}>
          <div className="flex justify-between mb-2 px-2">
            <span className="text-xs font-medium" style={{ color: theme.colors.textMuted }}>{data.scaleLabels.low}</span>
            <span className="text-xs font-medium" style={{ color: theme.colors.textMuted }}>{data.scaleLabels.high}</span>
          </div>
          <div className="flex justify-center gap-3">
            {points.map((point) => (
              <button key={point} onClick={() => { if (!submitted) setSelectedValue(point); }}
                disabled={submitted}
                className={`w-12 h-12 rounded-full border-2 text-sm font-bold transition-all ${
                  selectedValue === point
                    ? 'scale-110 shadow-lg'
                    : 'hover:scale-105'
                }`}
                style={{
                  borderColor: selectedValue === point ? theme.colors.primary : theme.colors.surface,
                  backgroundColor: selectedValue === point ? theme.colors.primary : theme.colors.surface,
                  color: selectedValue === point ? '#fff' : theme.colors.text,
                  opacity: submitted && selectedValue !== point ? 0.4 : 1,
                }}>
                {point}
              </button>
            ))}
          </div>
        </motion.div>
        {selectedValue !== null && !submitted && (
          <motion.div className="flex justify-center mt-6" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <button onClick={() => { setSubmitted(true); onActivityResponse?.({ activityType: 'likert', mode: 'survey', responseData: { value: selectedValue } }); }}
              className="px-6 py-2.5 rounded-lg text-sm font-medium text-white transition-colors" style={{ backgroundColor: theme.colors.primary }}>
              Submit
            </button>
          </motion.div>
        )}
        {submitted && <div className="flex justify-center"><SurveySubmittedMessage theme={theme} /></div>}
      </div>
    </div>
  );
}

// --- Survey Choice ---
function SurveyChoiceActivity({
  data, theme, isEditing, onUpdate, onActivityResponse,
}: {
  data: SurveyChoiceSlideData; theme: Theme; isEditing: boolean;
  onUpdate: (data: SurveyChoiceSlideData) => void;
  onActivityResponse?: (response: ActivityResponsePayload) => void;
}) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [submitted, setSubmitted] = useState(false);

  const addOption = (afterIndex?: number) => {
    const newOpt = { id: crypto.randomUUID(), text: '' };
    if (afterIndex !== undefined) {
      const newOptions = [...data.options];
      newOptions.splice(afterIndex + 1, 0, newOpt);
      onUpdate({ ...data, options: newOptions });
    } else {
      onUpdate({ ...data, options: [...data.options, newOpt] });
    }
    setTimeout(() => { const inputs = document.querySelectorAll<HTMLInputElement>('[data-survey-option]'); inputs[afterIndex !== undefined ? afterIndex + 1 : inputs.length - 1]?.focus(); }, 50);
  };

  const removeOption = (id: string) => {
    onUpdate({ ...data, options: data.options.filter((o) => o.id !== id) });
  };

  const handleOptionKeyDown = (e: React.KeyboardEvent, id: string, index: number) => {
    if (e.key === 'Enter') { e.preventDefault(); addOption(index); }
    else if (e.key === 'Backspace' && data.options[index].text === '' && data.options.length > 1) { e.preventDefault(); removeOption(id); }
  };

  const toggleSelect = (id: string) => {
    if (submitted) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (data.allowMultiple) { next.has(id) ? next.delete(id) : next.add(id); }
      else { next.clear(); next.add(id); }
      return next;
    });
  };

  if (isEditing) {
    return (
      <div className="h-full p-8 md:p-16 flex flex-col justify-center">
        <WrappingInput value={data.question} onChange={(question) => onUpdate({ ...data, question })}
          placeholder="Enter your question..." className="w-full mb-6 bg-transparent border-0 border-b-2 border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none transition-colors"
          data-slide-level="heading" style={{ ...slideTypographyStyle(theme, 'heading'), color: theme.colors.text }} />
        <label className="flex items-center gap-2 mb-4 cursor-pointer">
          <input type="checkbox" checked={data.allowMultiple} onChange={(e) => onUpdate({ ...data, allowMultiple: e.target.checked })}
            className="rounded border-gray-300" />
          <span className="text-sm" style={{ color: theme.colors.textMuted }}>Allow multiple selections</span>
        </label>
        <div className="space-y-3">
          {data.options.map((option, index) => (
            <div key={option.id} className="flex items-center gap-3 group">
              <input type="text" data-survey-option value={option.text}
                onChange={(e) => onUpdate({ ...data, options: data.options.map((o) => o.id === option.id ? { ...o, text: e.target.value } : o) })}
                onKeyDown={(e) => handleOptionKeyDown(e, option.id, index)}
                placeholder={`Option ${index + 1}... (Enter for new, Backspace to remove empty)`}
                className="flex-1 px-4 py-3 rounded-lg border-2 border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                style={{ backgroundColor: theme.colors.surface, color: theme.colors.text }} />
              <button onClick={() => removeOption(option.id)} className="p-1 text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all">×</button>
            </div>
          ))}
        </div>
        <button onClick={() => addOption()} className="mt-4 px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">+ Add option</button>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col justify-center items-center" style={{ padding: theme.spacing.padded }}>
      <div className="slide-content-narrow">
        <motion.h2 className="mb-8" initial={{ opacity: 0, y: 25 }} animate={{ opacity: 1, y: 0 }} transition={springs.gentle as object}
          data-slide-level="heading" style={{ ...slideTypographyStyle(theme, 'heading'), color: theme.colors.text }}>
          {data.question || 'Survey Choice'}
        </motion.h2>
        <div className="space-y-3">
          {data.options.map((option, i) => (
            <motion.button key={option.id} onClick={() => toggleSelect(option.id)} disabled={submitted}
              className={`w-full text-left px-5 py-3.5 rounded-xl border-2 transition-all ${selectedIds.has(option.id) ? 'shadow-md' : 'hover:shadow-sm'}`}
              initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ ...springs.gentle as object, delay: i * 0.05 }}
              style={{
                borderColor: selectedIds.has(option.id) ? theme.colors.primary : 'transparent',
                backgroundColor: selectedIds.has(option.id) ? theme.colors.primary + '10' : theme.colors.surface,
                color: theme.colors.text,
                opacity: submitted && !selectedIds.has(option.id) ? 0.4 : 1,
              }}>
              <span className="text-sm font-medium">{option.text}</span>
            </motion.button>
          ))}
        </div>
        {selectedIds.size > 0 && !submitted && (
          <motion.div className="flex justify-center mt-6" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <button onClick={() => { setSubmitted(true); onActivityResponse?.({ activityType: 'survey-choice', mode: 'survey', responseData: { selectedOptionIds: Array.from(selectedIds) } }); }}
              className="px-6 py-2.5 rounded-lg text-sm font-medium text-white transition-colors" style={{ backgroundColor: theme.colors.primary }}>Submit</button>
          </motion.div>
        )}
        {submitted && <div className="flex justify-center"><SurveySubmittedMessage theme={theme} /></div>}
      </div>
    </div>
  );
}

// --- Free Text ---
function FreeTextActivity({
  data, theme, isEditing, onUpdate, onActivityResponse,
}: {
  data: FreeTextSlideData; theme: Theme; isEditing: boolean;
  onUpdate: (data: FreeTextSlideData) => void;
  onActivityResponse?: (response: ActivityResponsePayload) => void;
}) {
  const [text, setText] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const autoGrow = useCallback(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = Math.max(ta.scrollHeight, 160) + 'px';
  }, []);

  useEffect(() => { autoGrow(); }, [text, autoGrow]);

  if (isEditing) {
    return (
      <div className="h-full p-8 md:p-16 flex flex-col justify-center">
        <WrappingInput value={data.question} onChange={(question) => onUpdate({ ...data, question })}
          placeholder="Enter your question..." className="w-full mb-6 bg-transparent border-0 border-b-2 border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none transition-colors"
          data-slide-level="heading" style={{ ...slideTypographyStyle(theme, 'heading'), color: theme.colors.text }} />
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium mb-1" style={{ color: theme.colors.textMuted }}>Placeholder text</label>
            <input type="text" value={data.placeholder || ''} onChange={(e) => onUpdate({ ...data, placeholder: e.target.value })}
              placeholder="e.g., Type your response..." className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              style={{ backgroundColor: theme.colors.surface, color: theme.colors.text }} />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1" style={{ color: theme.colors.textMuted }}>Max character length (optional)</label>
            <input type="number" value={data.maxLength || ''} onChange={(e) => onUpdate({ ...data, maxLength: e.target.value ? parseInt(e.target.value) : undefined })}
              placeholder="No limit" className="w-32 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              style={{ backgroundColor: theme.colors.surface, color: theme.colors.text }} min={1} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col justify-center items-center" style={{ padding: theme.spacing.padded }}>
      <div className="slide-content-narrow">
        <motion.h2 className="mb-6" initial={{ opacity: 0, y: 25 }} animate={{ opacity: 1, y: 0 }} transition={springs.gentle as object}
          data-slide-level="heading" style={{ ...slideTypographyStyle(theme, 'heading'), color: theme.colors.text }}>
          {data.question || 'Free Text Response'}
        </motion.h2>
        <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ ...springs.gentle as object, delay: 0.1 }}>
          <textarea ref={textareaRef} value={text} onChange={(e) => { if (!submitted) setText(data.maxLength ? e.target.value.slice(0, data.maxLength) : e.target.value); }}
            placeholder={data.placeholder || 'Type your response...'}
            disabled={submitted}
            rows={6}
            className="w-full px-5 py-4 rounded-xl border-2 border-transparent focus:border-blue-500 focus:outline-none resize-y transition-all"
            style={{ ...slideTypographyStyle(theme, 'body', theme.fonts.body), backgroundColor: theme.colors.surface, color: theme.colors.text, minHeight: '160px', lineHeight: 1.6 }} />
          {data.maxLength && (
            <div className="text-right mt-1">
              <span className="text-xs" style={{ color: text.length > data.maxLength * 0.9 ? theme.colors.accent : theme.colors.textMuted }}>
                {text.length}/{data.maxLength}
              </span>
            </div>
          )}
        </motion.div>
        {text.trim().length > 0 && !submitted && (
          <motion.div className="flex justify-center mt-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <button onClick={() => { setSubmitted(true); onActivityResponse?.({ activityType: 'free-text', mode: 'survey', responseData: { text } }); }}
              className="px-6 py-2.5 rounded-lg text-sm font-medium text-white transition-colors" style={{ backgroundColor: theme.colors.primary }}>Submit</button>
          </motion.div>
        )}
        {submitted && <div className="flex justify-center"><SurveySubmittedMessage theme={theme} /></div>}
      </div>
    </div>
  );
}

// --- Dropdown ---
function DropdownActivity({
  data, theme, isEditing, onUpdate, onActivityResponse,
}: {
  data: DropdownSlideData; theme: Theme; isEditing: boolean;
  onUpdate: (data: DropdownSlideData) => void;
  onActivityResponse?: (response: ActivityResponsePayload) => void;
}) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [answerState, setAnswerState] = useState<'idle' | 'correct' | 'incorrect' | 'submitted'>('idle');
  const isQuiz = data.mode === 'quiz';

  const addOption = (afterIndex?: number) => {
    const newOpt = { id: crypto.randomUUID(), text: '', isCorrect: false };
    if (afterIndex !== undefined) {
      const newOptions = [...data.options];
      newOptions.splice(afterIndex + 1, 0, newOpt);
      onUpdate({ ...data, options: newOptions });
    } else {
      onUpdate({ ...data, options: [...data.options, newOpt] });
    }
    setTimeout(() => { const inputs = document.querySelectorAll<HTMLInputElement>('[data-dropdown-option]'); inputs[afterIndex !== undefined ? afterIndex + 1 : inputs.length - 1]?.focus(); }, 50);
  };

  const removeOption = (id: string) => {
    onUpdate({ ...data, options: data.options.filter((o) => o.id !== id) });
  };

  const handleOptionKeyDown = (e: React.KeyboardEvent, id: string, index: number) => {
    if (e.key === 'Enter') { e.preventDefault(); addOption(index); }
    else if (e.key === 'Backspace' && data.options[index].text === '' && data.options.length > 1) { e.preventDefault(); removeOption(id); }
  };

  if (isEditing) {
    return (
      <div className="h-full p-8 md:p-16 flex flex-col justify-center">
        <WrappingInput value={data.question} onChange={(question) => onUpdate({ ...data, question })}
          placeholder="Enter your question..." className="w-full mb-4 bg-transparent border-0 border-b-2 border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none transition-colors"
          data-slide-level="heading" style={{ ...slideTypographyStyle(theme, 'heading'), color: theme.colors.text }} />
        <div className="flex items-center gap-2 mb-6">
          <span className="text-xs font-medium" style={{ color: theme.colors.textMuted }}>Mode:</span>
          <div className="flex rounded-lg border border-gray-200 overflow-hidden">
            {(['survey', 'quiz'] as const).map((m) => (
              <button key={m} onClick={() => onUpdate({ ...data, mode: m })}
                className={`px-3 py-1 text-xs font-medium transition-colors ${data.mode === m ? 'bg-blue-50 text-blue-600' : 'text-gray-500 hover:bg-gray-50'}`}>
                {m.charAt(0).toUpperCase() + m.slice(1)}
              </button>
            ))}
          </div>
        </div>
        <div className="space-y-3">
          {data.options.map((option, index) => (
            <div key={option.id}>
              <div className="flex items-center gap-3 group">
                {isQuiz && (
                  <label className="relative flex items-center gap-1 cursor-pointer shrink-0" title="Mark as correct answer">
                    <input type="checkbox" checked={option.isCorrect}
                      onChange={(e) => onUpdate({ ...data, options: data.options.map((o) => o.id === option.id ? { ...o, isCorrect: e.target.checked } : o) })}
                      className="sr-only peer" />
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${option.isCorrect ? 'border-green-500 bg-green-500' : 'border-gray-300 hover:border-green-400'}`}>
                      {option.isCorrect && <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                    </div>
                  </label>
                )}
                <input type="text" data-dropdown-option value={option.text}
                  onChange={(e) => onUpdate({ ...data, options: data.options.map((o) => o.id === option.id ? { ...o, text: e.target.value } : o) })}
                  onKeyDown={(e) => handleOptionKeyDown(e, option.id, index)}
                  placeholder={`Option ${index + 1}...`}
                  className="flex-1 px-4 py-3 rounded-lg border-2 border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                  style={{ backgroundColor: theme.colors.surface, color: theme.colors.text }} />
                <button onClick={() => removeOption(option.id)} className="p-1 text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all">×</button>
              </div>
              {isQuiz && !option.isCorrect && (
                <div className="ml-[7.5rem] mr-8 mt-1">
                  <FeedbackSelector
                    value={option.feedback || ''}
                    onChange={(fb) => onUpdate({ ...data, options: data.options.map((o) => o.id === option.id ? { ...o, feedback: fb } : o) })}
                    theme={theme}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
        <button onClick={() => addOption()} className="mt-4 px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">+ Add option</button>
      </div>
    );
  }

  const handleSubmit = () => {
    if (!selectedId) return;
    if (isQuiz) {
      const selected = data.options.find((o) => o.id === selectedId);
      const correct = !!selected?.isCorrect;
      setAnswerState(correct ? 'correct' : 'incorrect');
      if (correct) fireCorrect();
      onActivityResponse?.({ activityType: 'dropdown', mode: 'quiz', responseData: { selectedOptionId: selectedId }, isCorrect: correct, score: correct ? 100 : 0 });
    } else {
      setAnswerState('submitted');
      onActivityResponse?.({ activityType: 'dropdown', mode: 'survey', responseData: { selectedOptionId: selectedId } });
    }
  };

  return (
    <div className="h-full flex flex-col justify-center items-center" style={{ padding: theme.spacing.padded }}>
      <div className="slide-content-narrow">
        <motion.h2 className="mb-8" initial={{ opacity: 0, y: 25 }} animate={{ opacity: 1, y: 0 }} transition={springs.gentle as object}
          data-slide-level="heading" style={{ ...slideTypographyStyle(theme, 'heading'), color: theme.colors.text }}>
          {data.question || 'Dropdown'}
        </motion.h2>
        <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ ...springs.gentle as object, delay: 0.1 }}>
          <select value={selectedId || ''} onChange={(e) => { if (answerState === 'idle') setSelectedId(e.target.value || null); }}
            disabled={answerState !== 'idle'}
            className="w-full px-4 py-3 rounded-xl border-2 border-transparent focus:border-blue-500 focus:outline-none text-sm transition-all appearance-none cursor-pointer"
            style={{ backgroundColor: theme.colors.surface, color: selectedId ? theme.colors.text : theme.colors.textMuted, fontFamily: theme.fonts.body }}>
            <option value="">Select an option...</option>
            {data.options.map((opt) => <option key={opt.id} value={opt.id}>{opt.text}</option>)}
          </select>
        </motion.div>
        {selectedId && answerState === 'idle' && (
          <motion.div className="flex justify-center mt-6" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <button onClick={handleSubmit} className="px-6 py-2.5 rounded-lg text-sm font-medium text-white transition-colors" style={{ backgroundColor: theme.colors.primary }}>
              {isQuiz ? 'Check Answer' : 'Submit'}
            </button>
          </motion.div>
        )}
        {answerState === 'correct' && (
          <motion.div className="flex items-center justify-center gap-2 mt-6 text-green-600" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}>
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
            <span className="text-sm font-medium">Correct!</span>
          </motion.div>
        )}
        {answerState === 'incorrect' && (
          <motion.div className="flex flex-col items-center gap-2 mt-6" style={{ color: theme.colors.feedback.incorrectText }} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}>
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" /></svg>
              <span className="text-sm font-medium">
                {selectedId && data.options.find(o => o.id === selectedId)?.feedback
                  ? data.options.find(o => o.id === selectedId)!.feedback
                  : 'Not quite — try again!'}
              </span>
              <button onClick={() => { setAnswerState('idle'); setSelectedId(null); }} className="ml-2 text-xs underline">Retry</button>
            </div>
          </motion.div>
        )}
        {answerState === 'submitted' && <div className="flex justify-center"><SurveySubmittedMessage theme={theme} /></div>}
      </div>
    </div>
  );
}

// --- Ranking ---
function RankingActivity({
  data, theme, isEditing, onUpdate, onActivityResponse,
}: {
  data: RankingSlideData; theme: Theme; isEditing: boolean;
  onUpdate: (data: RankingSlideData) => void;
  onActivityResponse?: (response: ActivityResponsePayload) => void;
}) {
  // Shuffle items for the student on first render
  const [studentOrder, setStudentOrder] = useState<Array<{ id: string; text: string }>>(() => {
    const shuffled = [...data.items];
    for (let i = shuffled.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]; }
    return shuffled;
  });
  const [answerState, setAnswerState] = useState<'idle' | 'correct' | 'incorrect' | 'submitted'>('idle');
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const isQuiz = data.mode === 'quiz';

  const addItem = (afterIndex?: number) => {
    const newItem = { id: crypto.randomUUID(), text: '' };
    if (afterIndex !== undefined) {
      const newItems = [...data.items];
      newItems.splice(afterIndex + 1, 0, newItem);
      onUpdate({ ...data, items: newItems });
    } else {
      onUpdate({ ...data, items: [...data.items, newItem] });
    }
    setTimeout(() => { const inputs = document.querySelectorAll<HTMLInputElement>('[data-ranking-item]'); inputs[afterIndex !== undefined ? afterIndex + 1 : inputs.length - 1]?.focus(); }, 50);
  };

  const removeItem = (id: string) => {
    onUpdate({ ...data, items: data.items.filter((i) => i.id !== id) });
  };

  const handleItemKeyDown = (e: React.KeyboardEvent, id: string, index: number) => {
    if (e.key === 'Enter') { e.preventDefault(); addItem(index); }
    else if (e.key === 'Backspace' && data.items[index].text === '' && data.items.length > 1) { e.preventDefault(); removeItem(id); }
  };

  const moveItem = (fromIndex: number, toIndex: number) => {
    if (answerState !== 'idle') return;
    const newOrder = [...studentOrder];
    const [moved] = newOrder.splice(fromIndex, 1);
    newOrder.splice(toIndex, 0, moved);
    setStudentOrder(newOrder);
  };

  if (isEditing) {
    return (
      <div className="h-full p-8 md:p-16 flex flex-col justify-center">
        <WrappingInput value={data.question} onChange={(question) => onUpdate({ ...data, question })}
          placeholder="Enter your question..." className="w-full mb-4 bg-transparent border-0 border-b-2 border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none transition-colors"
          data-slide-level="heading" style={{ ...slideTypographyStyle(theme, 'heading'), color: theme.colors.text }} />
        <div className="flex items-center gap-2 mb-6">
          <span className="text-xs font-medium" style={{ color: theme.colors.textMuted }}>Mode:</span>
          <div className="flex rounded-lg border border-gray-200 overflow-hidden">
            {(['survey', 'quiz'] as const).map((m) => (
              <button key={m} onClick={() => onUpdate({ ...data, mode: m })}
                className={`px-3 py-1 text-xs font-medium transition-colors ${data.mode === m ? 'bg-blue-50 text-blue-600' : 'text-gray-500 hover:bg-gray-50'}`}>
                {m.charAt(0).toUpperCase() + m.slice(1)}
              </button>
            ))}
          </div>
        </div>
        <p className="text-xs mb-3" style={{ color: theme.colors.textMuted }}>
          {isQuiz ? 'Items listed top-to-bottom = correct order' : 'Add items for students to rank'}
        </p>
        <div className="space-y-3">
          {data.items.map((item, index) => (
            <div key={item.id} className="flex items-center gap-3 group">
              <span className="text-xs font-bold w-6 text-center" style={{ color: theme.colors.textMuted }}>{index + 1}</span>
              <input type="text" data-ranking-item value={item.text}
                onChange={(e) => onUpdate({ ...data, items: data.items.map((i) => i.id === item.id ? { ...i, text: e.target.value } : i) })}
                onKeyDown={(e) => handleItemKeyDown(e, item.id, index)}
                placeholder={`Item ${index + 1}...`}
                className="flex-1 px-4 py-3 rounded-lg border-2 border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                style={{ backgroundColor: theme.colors.surface, color: theme.colors.text }} />
              <button onClick={() => removeItem(item.id)} className="p-1 text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all">×</button>
            </div>
          ))}
        </div>
        <button onClick={() => addItem()} className="mt-4 px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">+ Add item</button>
      </div>
    );
  }

  const handleSubmit = () => {
    const orderedIds = studentOrder.map((i) => i.id);
    if (isQuiz) {
      const correctIds = data.items.map((i) => i.id);
      const isCorrect = JSON.stringify(orderedIds) === JSON.stringify(correctIds);
      const correctCount = orderedIds.filter((id, idx) => id === correctIds[idx]).length;
      const score = Math.round((correctCount / correctIds.length) * 100);
      setAnswerState(isCorrect ? 'correct' : 'incorrect');
      if (isCorrect) fireCorrect();
      onActivityResponse?.({ activityType: 'ranking', mode: 'quiz', responseData: { orderedItemIds: orderedIds }, isCorrect, score });
    } else {
      setAnswerState('submitted');
      onActivityResponse?.({ activityType: 'ranking', mode: 'survey', responseData: { orderedItemIds: orderedIds } });
    }
  };

  return (
    <div className="h-full flex flex-col justify-center items-center" style={{ padding: theme.spacing.padded }}>
      <div className="slide-content-narrow">
        <motion.h2 className="mb-6" initial={{ opacity: 0, y: 25 }} animate={{ opacity: 1, y: 0 }} transition={springs.gentle as object}
          data-slide-level="heading" style={{ ...slideTypographyStyle(theme, 'heading'), color: theme.colors.text }}>
          {data.question || 'Ranking'}
        </motion.h2>
        <p className="text-sm mb-4" style={{ color: theme.colors.textMuted }}>Drag items to reorder</p>
        <div className="space-y-2">
          {studentOrder.map((item, index) => {
            const correctIndex = data.items.findIndex((i) => i.id === item.id);
            const isInCorrectPos = isQuiz && answerState !== 'idle' && correctIndex === index;
            const isWrongPos = isQuiz && answerState === 'incorrect' && correctIndex !== index;
            return (
              <motion.div
                key={item.id}
                className="flex items-center gap-3 px-4 py-3 rounded-xl border-2 cursor-grab active:cursor-grabbing transition-all"
                style={{
                  backgroundColor: isInCorrectPos
                    ? theme.colors.feedback.correctBg
                    : isWrongPos
                      ? theme.colors.feedback.incorrectBg
                      : theme.colors.surface,
                  borderColor: isInCorrectPos
                    ? theme.colors.feedback.correctBorder
                    : isWrongPos
                      ? theme.colors.feedback.incorrectBorder
                      : 'transparent',
                  color: isInCorrectPos
                    ? theme.colors.feedback.correctText
                    : isWrongPos
                      ? theme.colors.feedback.incorrectText
                      : theme.colors.text,
                  opacity: dragIndex !== null && dragIndex !== index ? 0.6 : 1,
                }}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ ...springs.gentle as object, delay: index * 0.04 }}
                draggable={answerState === 'idle'}
                onDragStart={() => setDragIndex(index)}
                onDragOver={(e) => { e.preventDefault(); if (dragIndex !== null && dragIndex !== index) moveItem(dragIndex, index); setDragIndex(index); }}
                onDragEnd={() => setDragIndex(null)}
              >
                <span className="text-xs font-bold w-6 text-center" style={{ color: theme.colors.textMuted }}>{index + 1}</span>
                <span className="flex-1 text-sm font-medium">{item.text}</span>
                {answerState === 'idle' && (
                  <div className="flex flex-col gap-0.5">
                    <button onClick={() => index > 0 && moveItem(index, index - 1)} disabled={index === 0}
                      className="p-0.5 hover:bg-black/5 rounded disabled:opacity-20" style={{ color: theme.colors.textMuted }}>
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" /></svg>
                    </button>
                    <button onClick={() => index < studentOrder.length - 1 && moveItem(index, index + 1)} disabled={index === studentOrder.length - 1}
                      className="p-0.5 hover:bg-black/5 rounded disabled:opacity-20" style={{ color: theme.colors.textMuted }}>
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                    </button>
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
        {answerState === 'idle' && studentOrder.length > 0 && (
          <motion.div className="flex justify-center mt-6" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <button onClick={handleSubmit} className="px-6 py-2.5 rounded-lg text-sm font-medium text-white transition-colors" style={{ backgroundColor: theme.colors.primary }}>
              {isQuiz ? 'Check Answer' : 'Submit'}
            </button>
          </motion.div>
        )}
        {answerState === 'correct' && (
          <motion.div className="flex items-center justify-center gap-2 mt-6 text-green-600" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}>
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
            <span className="text-sm font-medium">Perfect order!</span>
          </motion.div>
        )}
        {answerState === 'incorrect' && (
          <motion.div className="flex items-center justify-center gap-2 mt-6 text-red-500" initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}>
            <span className="text-sm font-medium">Not quite right</span>
            <button onClick={() => { setAnswerState('idle'); }} className="ml-2 text-xs underline">Retry</button>
          </motion.div>
        )}
        {answerState === 'submitted' && <div className="flex justify-center"><SurveySubmittedMessage theme={theme} /></div>}
      </div>
    </div>
  );
}

// --- Matching ---
function MatchingActivity({
  data, theme, isEditing, onUpdate, onActivityResponse,
}: {
  data: MatchingSlideData; theme: Theme; isEditing: boolean;
  onUpdate: (data: MatchingSlideData) => void;
  onActivityResponse?: (response: ActivityResponsePayload) => void;
}) {
  // Shuffle right column for the student
  const [shuffledRight] = useState<Array<{ id: string; right: string }>>(() => {
    const items = data.pairs.map((p) => ({ id: p.id, right: p.right }));
    for (let i = items.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [items[i], items[j]] = [items[j], items[i]]; }
    return items;
  });
  const [connections, setConnections] = useState<Record<string, string>>({});
  const [selectedLeft, setSelectedLeft] = useState<string | null>(null);
  const [answerState, setAnswerState] = useState<'idle' | 'checked'>('idle');
  const [pairResults, setPairResults] = useState<Record<string, boolean>>({});

  const addPair = () => {
    onUpdate({ ...data, pairs: [...data.pairs, { id: crypto.randomUUID(), left: '', right: '' }] });
  };

  const removePair = (id: string) => {
    onUpdate({ ...data, pairs: data.pairs.filter((p) => p.id !== id) });
  };

  const handleLeftClick = (pairId: string) => {
    if (answerState !== 'idle') return;
    if (selectedLeft === pairId) { setSelectedLeft(null); return; }
    setSelectedLeft(pairId);
  };

  const handleRightClick = (pairId: string) => {
    if (answerState !== 'idle' || !selectedLeft) return;
    // Remove existing connection to this right item
    const newConnections = { ...connections };
    for (const [k, v] of Object.entries(newConnections)) { if (v === pairId) delete newConnections[k]; }
    newConnections[selectedLeft] = pairId;
    setConnections(newConnections);
    setSelectedLeft(null);
  };

  const handleCheckAnswer = () => {
    const results: Record<string, boolean> = {};
    let correctCount = 0;
    data.pairs.forEach((pair) => {
      const isCorrect = connections[pair.id] === pair.id;
      results[pair.id] = isCorrect;
      if (isCorrect) correctCount++;
    });
    setPairResults(results);
    setAnswerState('checked');
    const allCorrect = correctCount === data.pairs.length;
    if (allCorrect) fireCorrect();
    const score = Math.round((correctCount / data.pairs.length) * 100);
    onActivityResponse?.({
      activityType: 'matching', mode: 'quiz',
      responseData: { connections },
      isCorrect: allCorrect, score,
    });
  };

  if (isEditing) {
    return (
      <div className="h-full p-8 md:p-16 flex flex-col justify-center">
        <WrappingInput value={data.question} onChange={(question) => onUpdate({ ...data, question })}
          placeholder="Enter your question..." className="w-full mb-6 bg-transparent border-0 border-b-2 border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none transition-colors"
          data-slide-level="heading" style={{ ...slideTypographyStyle(theme, 'heading'), color: theme.colors.text }} />
        <p className="text-xs mb-3" style={{ color: theme.colors.textMuted }}>Each row is a correct pair. Right column will be shuffled for students.</p>
        <div className="space-y-3">
          {data.pairs.map((pair, index) => (
            <div key={pair.id} className="flex items-center gap-3 group">
              <span className="text-xs font-bold w-6 text-center" style={{ color: theme.colors.textMuted }}>{index + 1}</span>
              <input type="text" value={pair.left}
                onChange={(e) => onUpdate({ ...data, pairs: data.pairs.map((p) => p.id === pair.id ? { ...p, left: e.target.value } : p) })}
                placeholder="Left item..." className="flex-1 px-4 py-3 rounded-lg border-2 border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                style={{ backgroundColor: theme.colors.surface, color: theme.colors.text }} />
              <svg className="w-5 h-5 shrink-0" style={{ color: theme.colors.textMuted }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
              <input type="text" value={pair.right}
                onChange={(e) => onUpdate({ ...data, pairs: data.pairs.map((p) => p.id === pair.id ? { ...p, right: e.target.value } : p) })}
                placeholder="Right match..." className="flex-1 px-4 py-3 rounded-lg border-2 border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                style={{ backgroundColor: theme.colors.surface, color: theme.colors.text }} />
              <button onClick={() => removePair(pair.id)} className="p-1 text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all">×</button>
            </div>
          ))}
        </div>
        <button onClick={addPair} className="mt-4 px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">+ Add pair</button>
      </div>
    );
  }

  // View mode — click-to-connect
  const connectedRightIds = new Set(Object.values(connections));

  return (
    <div className="h-full flex flex-col justify-center items-center" style={{ padding: theme.spacing.padded }}>
      <div className="w-full slide-content-medium">
        <motion.h2 className="mb-6 text-center" initial={{ opacity: 0, y: 25 }} animate={{ opacity: 1, y: 0 }} transition={springs.gentle as object}
          data-slide-level="heading" style={{ ...slideTypographyStyle(theme, 'heading'), color: theme.colors.text }}>
          {data.question || 'Mix & Match'}
        </motion.h2>
        <p className="text-sm text-center mb-6" style={{ color: theme.colors.textMuted }}>Click a left item, then click its matching right item</p>
        <div className="flex gap-8 justify-center">
          {/* Left column */}
          <div className="space-y-3 flex-1 max-w-xs">
            {data.pairs.map((pair, i) => {
              const isConnected = pair.id in connections;
              const isSelected = selectedLeft === pair.id;
              const result = pairResults[pair.id];
              return (
                <motion.button key={pair.id} onClick={() => handleLeftClick(pair.id)}
                  className={`w-full text-left px-4 py-3 rounded-xl border-2 text-sm font-medium transition-all ${
                    answerState === 'checked'
                      ? ''
                      : isSelected ? 'shadow-md' : isConnected ? '' : 'hover:shadow-sm'
                  }`}
                  initial={{ opacity: 0, x: -15 }} animate={{ opacity: 1, x: 0 }} transition={{ ...springs.gentle as object, delay: i * 0.05 }}
                  style={{
                    borderColor: answerState === 'checked'
                      ? (result ? theme.colors.feedback.correctBorder : theme.colors.feedback.incorrectBorder)
                      : isSelected ? theme.colors.primary : isConnected ? theme.colors.accent : 'transparent',
                    backgroundColor: answerState === 'checked'
                      ? (result ? theme.colors.feedback.correctBg : theme.colors.feedback.incorrectBg)
                      : isSelected ? theme.colors.primary + '10' : theme.colors.surface,
                    color: answerState === 'checked'
                      ? (result ? theme.colors.feedback.correctText : theme.colors.feedback.incorrectText)
                      : theme.colors.text,
                  }}>
                  {pair.left}
                  {isConnected && answerState === 'idle' && (
                    <span className="ml-2 text-xs" style={{ color: theme.colors.accent }}>
                      → {shuffledRight.find((r) => r.id === connections[pair.id])?.right}
                    </span>
                  )}
                </motion.button>
              );
            })}
          </div>
          {/* Right column */}
          <div className="space-y-3 flex-1 max-w-xs">
            {shuffledRight.map((item, i) => {
              const isConnected = connectedRightIds.has(item.id);
              const linkedLeftId = Object.entries(connections).find(([, v]) => v === item.id)?.[0];
              const result = linkedLeftId ? pairResults[linkedLeftId] : undefined;
              return (
                <motion.button key={item.id} onClick={() => handleRightClick(item.id)}
                  className={`w-full text-left px-4 py-3 rounded-xl border-2 text-sm font-medium transition-all ${
                    answerState === 'checked'
                      ? ''
                      : isConnected ? '' : selectedLeft ? 'hover:shadow-sm ring-1 ring-blue-200' : ''
                  }`}
                  initial={{ opacity: 0, x: 15 }} animate={{ opacity: 1, x: 0 }} transition={{ ...springs.gentle as object, delay: i * 0.05 }}
                  style={{
                    borderColor: answerState === 'checked'
                      ? (result ? theme.colors.feedback.correctBorder : result === false ? theme.colors.feedback.incorrectBorder : 'transparent')
                      : isConnected ? theme.colors.accent : 'transparent',
                    backgroundColor: answerState === 'checked'
                      ? (result ? theme.colors.feedback.correctBg : result === false ? theme.colors.feedback.incorrectBg : theme.colors.surface)
                      : theme.colors.surface,
                    color: answerState === 'checked'
                      ? (result ? theme.colors.feedback.correctText : result === false ? theme.colors.feedback.incorrectText : theme.colors.text)
                      : theme.colors.text,
                  }}
                  disabled={answerState !== 'idle' || !selectedLeft}>
                  {item.right}
                </motion.button>
              );
            })}
          </div>
        </div>
        {answerState === 'idle' && Object.keys(connections).length === data.pairs.length && (
          <motion.div className="flex justify-center mt-6" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <button onClick={handleCheckAnswer} className="px-6 py-2.5 rounded-lg text-sm font-medium text-white transition-colors" style={{ backgroundColor: theme.colors.primary }}>
              Check Answer
            </button>
          </motion.div>
        )}
        {answerState === 'checked' && (
          <motion.div className="flex items-center justify-center gap-2 mt-6" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
            style={{ color: Object.values(pairResults).every(Boolean) ? '#16a34a' : '#ef4444' }}>
            <span className="text-sm font-medium">
              {Object.values(pairResults).filter(Boolean).length}/{data.pairs.length} correct
            </span>
            {!Object.values(pairResults).every(Boolean) && (
              <button onClick={() => { setAnswerState('idle'); setConnections({}); setPairResults({}); setSelectedLeft(null); }}
                className="ml-2 text-xs underline" style={{ color: theme.colors.textMuted }}>Retry</button>
            )}
          </motion.div>
        )}
      </div>
    </div>
  );
}

// --- Activity Slide Dispatcher ---
export function ActivitySlide({
  data, theme, isEditing, onUpdate, onActivityResponse,
}: {
  data: ActivitySlideData; theme: Theme; isEditing: boolean;
  onUpdate: (data: ActivitySlideData) => void;
  onActivityResponse?: (response: ActivityResponsePayload) => void;
}) {
  switch (data.activityType) {
    case 'likert':
      return <LikertActivity data={data} theme={theme} isEditing={isEditing} onUpdate={onUpdate as (d: LikertSlideData) => void} onActivityResponse={onActivityResponse} />;
    case 'survey-choice':
      return <SurveyChoiceActivity data={data} theme={theme} isEditing={isEditing} onUpdate={onUpdate as (d: SurveyChoiceSlideData) => void} onActivityResponse={onActivityResponse} />;
    case 'free-text':
      return <FreeTextActivity data={data} theme={theme} isEditing={isEditing} onUpdate={onUpdate as (d: FreeTextSlideData) => void} onActivityResponse={onActivityResponse} />;
    case 'dropdown':
      return <DropdownActivity data={data} theme={theme} isEditing={isEditing} onUpdate={onUpdate as (d: DropdownSlideData) => void} onActivityResponse={onActivityResponse} />;
    case 'ranking':
      return <RankingActivity data={data} theme={theme} isEditing={isEditing} onUpdate={onUpdate as (d: RankingSlideData) => void} onActivityResponse={onActivityResponse} />;
    case 'matching':
      return <MatchingActivity data={data} theme={theme} isEditing={isEditing} onUpdate={onUpdate as (d: MatchingSlideData) => void} onActivityResponse={onActivityResponse} />;
    default:
      return <div className="h-full flex items-center justify-center" style={{ color: theme.colors.textMuted }}>Unknown activity type</div>;
  }
}
